import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import "./AdministradorDependencias.css";

// Pasá la URL base de tu API mediante la prop apiBaseUrl.
// Las rutas corresponden a las funciones req/res que definimos.
const ENDPOINTS = {
  arbol: "/dependencias/arbol",
  listado: "/dependencias",
  crear: "/dependencias/crear",
  modificar: "/dependencias/modificar",
  vincular: "/dependencias/vincular",
  usuarios: "/dependencias/usuarios",
};

const ROOT_ID = 1;
const DEFAULT_AXIOS_CONFIG = {};
const getId = (n) => Number(n.id);

// Estadísticas calculadas por el backend. No se suman en el frontend
// para evitar contar dos veces a los agentes de dependencias hijas.
const SEDES = { "1": "Provincial", "2": "Rosario", "3": "Recreo", "4": "Reconquista", sin_sede: "Sin sede" };
const cantidad = (valor) => Number.isFinite(Number(valor)) ? Number(valor) : 0;

function PersonalDependencia({ nodo, compacto = false }) {
  const disponible = nodo.personalDirecto !== undefined || nodo.personalTotal !== undefined;
  if (!disponible) return <small className="ad-personal-pendiente">Usuarios: estadísticas no disponibles</small>;
  const directo = cantidad(nodo.personalDirecto);
  const total = cantidad(nodo.personalTotal ?? nodo.personalDirecto);
  const porSede = nodo.personalPorSede || nodo.personalDirectoPorSede || {};
  const sedes = Object.entries(porSede).filter(([, n]) => cantidad(n) > 0)
    .sort(([a], [b]) => Number(a) - Number(b));
  return (
    <div className={`ad-personal ${compacto ? "ad-personal-compacto" : ""}`}>
      <div className="ad-personal-totales">
        <span title="Usuarios activos asignados directamente a este destino"><b>{directo}</b> usuarios directos</span>
        <span title="Usuarios activos de este destino y de todas sus subdependencias"><b>{total}</b> usuarios en total (incluye subordinados)</span>
      </div>
      {sedes.length > 0 ? (
        <div className="ad-personal-sedes" aria-label="Distribución de usuarios activos por sede">
          {sedes.map(([id, n]) => (
            <span key={id} title={`Personal total de la estructura en ${SEDES[id] || `Sede ${id}`}`}>
              {SEDES[id] || `Sede ${id}`}: <b>{cantidad(n)}</b> usuarios
            </span>
          ))}
        </div>
      ) : <small className="ad-personal-vacio">Sin usuarios activos asignados</small>}
    </div>
  );
}


// Reconstruye el organigrama desde la lista plana, utilizando el ID del padre.
// Soporta los nombres de columna habituales sin perder el identificador original.
function construirJerarquia(registros) {
  const mapa = new Map();
  for (const registro of registros) {
    const id = Number(registro.id);
    if (!Number.isSafeInteger(id)) continue;
    mapa.set(id, { ...registro, id, hijos: [] });
  }
  const raices = [];
  for (const nodo of mapa.values()) {
    const valorPadre = nodo.dependenciaPadreId ?? nodo.dependencia_padre_id ?? nodo.dependeDe ?? null;
    const padre = valorPadre === null || valorPadre === "" ? null : Number(valorPadre);
    nodo.dependenciaPadreId = padre;
    if (padre !== null && mapa.has(padre) && padre !== nodo.id) {
      mapa.get(padre).hijos.push(nodo);
    } else {
      raices.push(nodo);
    }
  }
  // Protección frente a ciclos presentes en registros antiguos.
  const visitados = new Set();
  const recorrer = (nodo, camino = new Set()) => {
    if (camino.has(nodo.id)) throw new Error("Existe una relación circular en las dependencias");
    if (visitados.has(nodo.id)) return;
    camino.add(nodo.id);
    visitados.add(nodo.id);
    nodo.hijos.forEach((hijo) => recorrer(hijo, camino));
    camino.delete(nodo.id);
  };
  raices.forEach((nodo) => recorrer(nodo));
  if (visitados.size !== mapa.size) throw new Error("Hay dependencias con vínculos circulares");
  return raices;
}

function aplanar(nodos, nivel = 0, salida = []) {
  for (const nodo of nodos) {
    salida.push({ ...nodo, nivel });
    aplanar(nodo.hijos || [], nivel + 1, salida);
  }
  return salida;
}

function descendientes(nodo, ids = new Set()) {
  for (const hijo of nodo.hijos || []) {
    ids.add(getId(hijo));
    descendientes(hijo, ids);
  }
  return ids;
}

function filtrarArbol(nodos, texto) {
  if (!texto.trim()) return nodos;
  const q = texto.toLocaleLowerCase("es").trim();
  return nodos.reduce((acc, nodo) => {
    const hijos = filtrarArbol(nodo.hijos || [], q);
    if (nodo.descripcion.toLocaleLowerCase("es").includes(q) || hijos.length) {
      acc.push({ ...nodo, hijos });
    }
    return acc;
  }, []);
}

function NodoArbol({ nodo, seleccionado, onSeleccionar, abiertos, alternar, busqueda, nivel = 0 }) {
  const tieneHijos = (nodo.hijos || []).length > 0;
  const expandido = Boolean(busqueda.trim()) || abiertos.has(getId(nodo));
  return (
    <div className="ad-nodo">
      <div className={`ad-nodo-fila ${seleccionado === getId(nodo) ? "ad-seleccionado" : ""}`} style={{ paddingLeft: `${12 + nivel * 19}px` }}>
        <button type="button" className="ad-expandir" onClick={() => alternar(getId(nodo))} disabled={!tieneHijos} aria-label={expandido ? "Contraer" : "Expandir"}>
          {tieneHijos ? (expandido ? "▾" : "▸") : "·"}
        </button>
        <button type="button" className="ad-nombre" onClick={() => onSeleccionar(getId(nodo))}>
          <span className="ad-icono">{getId(nodo) === ROOT_ID ? "▣" : tieneHijos ? "▤" : "▹"}</span>
          <span>{nodo.descripcion}</span>
          <small>#{nodo.id}</small>
          <PersonalDependencia nodo={nodo} compacto />
        </button>
      </div>
      {tieneHijos && expandido && (
        <div>
          {nodo.hijos.map((hijo) => (
            <NodoArbol key={hijo.id} nodo={hijo} seleccionado={seleccionado} onSeleccionar={onSeleccionar} abiertos={abiertos} alternar={alternar} busqueda={busqueda} nivel={nivel + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

const escaparHtml = (texto) => String(texto).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);

// Vista gráfica por niveles: cada tarjeta acepta soltar otra dependencia como hija.
function TarjetaJerarquia({ nodo, seleccionadoId, seleccionar, verPersonal, arrastradoId, setArrastradoId, destinoId, setDestinoId, soltar, bloqueadosAlArrastrar, nivel = 1, nivelesVisibles }) {
  const id = getId(nodo);
  const arrastrable = id !== ROOT_ID;
  const prohibido = arrastradoId != null && (id === arrastradoId || bloqueadosAlArrastrar.has(id));
  return (
    <div className="ad-organigrama-nodo">
      <div
        className={`ad-tarjeta ${id === ROOT_ID ? "ad-tarjeta-raiz" : ""} ${seleccionadoId === id ? "ad-tarjeta-seleccionada" : ""} ${destinoId === id ? "ad-tarjeta-destino" : ""} ${prohibido ? "ad-tarjeta-prohibida" : ""}`}
        draggable={arrastrable}
        onDragStart={(e) => {
          if (!arrastrable) return e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("text/plain", String(id));
          setArrastradoId(id);
        }}
        onDragEnd={() => { setArrastradoId(null); setDestinoId(null); }}
        onDragOver={(e) => {
          if (arrastradoId == null || prohibido) return;
          e.preventDefault(); e.stopPropagation();
          e.dataTransfer.dropEffect = "move";
          setDestinoId(id);
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setDestinoId((actual) => actual === id ? null : actual);
        }}
        onDrop={(e) => {
          e.preventDefault(); e.stopPropagation();
          const origen = Number(e.dataTransfer.getData("text/plain"));
          if (Number.isSafeInteger(origen) && !prohibido) soltar(origen, id);
          setArrastradoId(null); setDestinoId(null);
        }}
      >
        <button type="button" className="ad-tarjeta-contenido" onClick={() => seleccionar(id)} aria-label={`Seleccionar ${nodo.descripcion}`}>
          <span className="ad-tarjeta-icono">{id === ROOT_ID ? "▣" : "▤"}</span>
          <strong>{nodo.descripcion}</strong>
          <small>ID {id} · {(nodo.hijos || []).length} hijas</small>
          <PersonalDependencia nodo={nodo} />
        </button>
        <button type="button" className="ad-ver-personal ad-ver-personal-tarjeta" onClick={(e) => { e.stopPropagation(); verPersonal(id); }}>👥 Ver personal</button>
        {arrastrable && <span className="ad-asa" title="Arrastrá esta tarjeta sobre su nuevo superior">⠿</span>}
      </div>
      {nivelesVisibles.has(nivel + 1) && (nodo.hijos || []).length > 0 && (
        <div className="ad-organigrama-hijos">
          {(nodo.hijos || []).map((hijo) => (
            <TarjetaJerarquia key={hijo.id} nodo={hijo} seleccionadoId={seleccionadoId} seleccionar={seleccionar} verPersonal={verPersonal}
              arrastradoId={arrastradoId} setArrastradoId={setArrastradoId} destinoId={destinoId}
              setDestinoId={setDestinoId} soltar={soltar} bloqueadosAlArrastrar={bloqueadosAlArrastrar}
              nivel={nivel + 1} nivelesVisibles={nivelesVisibles} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdministradorDependencias({ apiBaseUrl = "", axiosConfig = DEFAULT_AXIOS_CONFIG }) {
  const navigate = useNavigate();
  const [arbol, setArbol] = useState([]);
  const [vista, setVista] = useState("lista");
  const [nivelesOcultos, setNivelesOcultos] = useState(new Set());
  const [arrastradoId, setArrastradoId] = useState(null);
  const [destinoId, setDestinoId] = useState(null);
  const [seleccionadoId, setSeleccionadoId] = useState(ROOT_ID);
  const [abiertos, setAbiertos] = useState(new Set([ROOT_ID]));
  const [busqueda, setBusqueda] = useState("");
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [modo, setModo] = useState("editar");
  const [descripcion, setDescripcion] = useState("");
  const [detalle, setDetalle] = useState("");
  const [padreId, setPadreId] = useState("");
  const [modalPersonal, setModalPersonal] = useState(null);
  const [datosPersonal, setDatosPersonal] = useState(null);
  const [cargandoPersonal, setCargandoPersonal] = useState(false);
  const [errorPersonal, setErrorPersonal] = useState("");
  const [filtroPersonal, setFiltroPersonal] = useState("");
  const [filtroSede, setFiltroSede] = useState("todas");
  const [filtroDestino, setFiltroDestino] = useState("todos");
  const [tipoPersonal, setTipoPersonal] = useState("todos");

  const api = useMemo(() => axios.create({ baseURL: apiBaseUrl, ...axiosConfig }), [apiBaseUrl, axiosConfig]);

  const verPersonal = async (id) => {
    const numero = Number(id);
    if (!Number.isSafeInteger(numero) || numero <= 0) return;
    setModalPersonal(numero);
    setDatosPersonal(null);
    setErrorPersonal("");
    setFiltroPersonal("");
    setFiltroSede("todas");
    setFiltroDestino("todos");
    setTipoPersonal("todos");
    setCargandoPersonal(true);
    try {
      const { data } = await api.post(ENDPOINTS.usuarios, { dependenciaId: numero });
      if (!data?.ok || !Array.isArray(data.usuarios)) throw new Error(data?.message || "Respuesta de usuarios inválida");
      setDatosPersonal(data);
    } catch (error) {
      setErrorPersonal(error.response?.data?.message || error.message || "No se pudo consultar el personal");
    } finally {
      setCargandoPersonal(false);
    }
  };

  const cerrarPersonal = () => { setModalPersonal(null); setDatosPersonal(null); };

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      // La lista plana es la fuente de verdad para los vínculos guardados en MySQL.
      // Evita depender de un árbol calculado por el servidor que pueda estar desactualizado.
      let registros;
      try {
        const respuesta = await api.post(ENDPOINTS.listado, { params: { _t: Date.now() } });
        registros = Array.isArray(respuesta.data) ? respuesta.data : respuesta.data?.data;
        if (!Array.isArray(registros)) throw new Error("El listado no devolvió un arreglo");
      } catch (errorListado) {
        // Compatibilidad con instalaciones que solo tienen la ruta de árbol.
        const { data } = await api.post(ENDPOINTS.arbol, { params: { _t: Date.now() } });
        if (!data?.ok || !Array.isArray(data.data)) throw new Error(data?.message || "Respuesta inválida del servidor");
        const juntar = (nodos) => nodos.flatMap((n) => [{ ...n, hijos: undefined }, ...juntar(n.hijos || [])]);
        registros = juntar(data.data);
      }
      const jerarquia = construirJerarquia(registros);
      setArbol(jerarquia);
    } catch (e) {
      Swal.fire("Error", e.response?.data?.message || e.message || "No se pudieron cargar las dependencias", "error");
    } finally {
      setCargando(false);
    }
  }, [api]);

  useEffect(() => { cargar(); }, [cargar]);

  const planos = useMemo(() => aplanar(arbol), [arbol]);
  const totalNiveles = Math.max(1, ...planos.map((d) => d.nivel + 1));
  const nivelesVisibles = useMemo(() => new Set(
    Array.from({ length: totalNiveles }, (_, i) => i + 1).filter((n) => !nivelesOcultos.has(n))
  ), [totalNiveles, nivelesOcultos]);
  const alternarNivel = (nivel) => setNivelesOcultos((anteriores) => {
    const siguientes = new Set(anteriores);
    if (siguientes.has(nivel)) siguientes.delete(nivel);
    else siguientes.add(nivel);
    return siguientes;
  });
  const seleccionado = planos.find((d) => getId(d) === seleccionadoId) || null;
  const raiz = planos.find((d) => getId(d) === ROOT_ID);
  const raizValida = Boolean(raiz);
  const bloqueados = useMemo(() => seleccionado ? descendientes(seleccionado) : new Set(), [seleccionado]);
  const opcionesPadre = planos.filter((d) => getId(d) !== seleccionadoId && !bloqueados.has(getId(d)));
  const bloqueadosAlArrastrar = useMemo(() => {
    if (arrastradoId == null) return new Set();
    const origen = planos.find((n) => getId(n) === arrastradoId);
    return origen ? descendientes(origen) : new Set();
  }, [arrastradoId, planos]);
  const visibles = useMemo(() => filtrarArbol(arbol, busqueda), [arbol, busqueda]);

  useEffect(() => {
    if (modo === "editar" && seleccionado) {
      setDescripcion(seleccionado.descripcion || "");
      setDetalle(seleccionado.detalle || "");
      setPadreId(seleccionado.dependenciaPadreId == null ? "" : String(seleccionado.dependenciaPadreId));
    }
  }, [seleccionadoId, seleccionado?.descripcion, seleccionado?.detalle, seleccionado?.dependenciaPadreId, modo]);

  const alternar = (id) => setAbiertos((prev) => {
    const copia = new Set(prev);
    if (copia.has(id)) copia.delete(id); else copia.add(id);
    return copia;
  });

  const seleccionar = (id) => {
    setSeleccionadoId(id);
    setModo("editar");
  };

  const iniciarCreacion = (subordinada) => {
    setModo("crear");
    setDescripcion("");
    setDetalle("");
    setPadreId(subordinada && seleccionado ? String(seleccionado.id) : String(ROOT_ID));
  };

  const manejarError = (error) => Swal.fire("No se pudo guardar", error.response?.data?.message || error.message || "Error inesperado", "error");

  const moverPorArrastre = async (origenId, nuevoPadreId) => {
    if (guardando || origenId === ROOT_ID || origenId === nuevoPadreId) return;
    const origen = planos.find((n) => getId(n) === origenId);
    const destino = planos.find((n) => getId(n) === nuevoPadreId);
    if (!origen || !destino || descendientes(origen).has(nuevoPadreId)) return;
    if (Number(origen.dependenciaPadreId) === nuevoPadreId) return;

    const confirmacion = await Swal.fire({
      title: "¿Cambiar dependencia superior?",
      html: `<strong>${escaparHtml(origen.descripcion)}</strong> pasará a depender de <strong>${escaparHtml(destino.descripcion)}</strong>.<br/><br/>Sus dependencias hijas se moverán con ella y cambiarán los permisos heredados.`,
      icon: "question", showCancelButton: true,
      confirmButtonText: "Sí, vincular", cancelButtonText: "Cancelar"
    });
    if (!confirmacion.isConfirmed) return;
    setGuardando(true);
    try {
      const { data } = await api.post(ENDPOINTS.vincular, { id: origenId, dependenciaPadreId: nuevoPadreId });
      if (!data?.ok) throw new Error(data?.message || "No se pudo vincular");
      setSeleccionadoId(origenId);
      setModo("editar");
      setAbiertos((prev) => new Set([...prev, nuevoPadreId]));
      await cargar();
      Swal.fire({ icon: "success", title: "Dependencia reubicada", timer: 1300, showConfirmButton: false });
    } catch (error) {
      manejarError(error);
      await cargar();
    } finally {
      setGuardando(false);
    }
  };

  const guardar = async (event) => {
    event.preventDefault();
    const nombre = descripcion.trim();
    const detalleNormalizado = detalle.trim();
    if (!nombre || nombre.length > 100) return Swal.fire("Validación", "Ingresá una descripción de 1 a 100 caracteres.", "warning");
    if (!raizValida) return Swal.fire("Falta la raíz", "Primero debe existir la dependencia ID 1.", "warning");
    if (modo === "crear" && !padreId) return Swal.fire("Validación", "Elegí una dependencia superior.", "warning");
    if (modo === "editar" && !seleccionado) return;
    if (modo === "editar" && seleccionadoId === ROOT_ID && padreId !== "") return Swal.fire("Validación", "La Dirección General (ID 1) debe permanecer como raíz.", "warning");

    const idPadre = padreId === "" ? null : Number(padreId);
    if (idPadre !== null && (!Number.isSafeInteger(idPadre) || idPadre <= 0)) return;

    setGuardando(true);
    try {
      if (modo === "crear") {
        const { data } = await api.post(ENDPOINTS.crear, { descripcion: nombre, detalle: detalleNormalizado || null, dependenciaPadreId: idPadre });
        if (!data.ok) throw new Error(data.message);
        setSeleccionadoId(Number(data.data.id));
        setAbiertos((prev) => new Set([...prev, idPadre]));
      } else {
        if (nombre !== seleccionado.descripcion || detalleNormalizado !== (seleccionado.detalle || "").trim()) {
          const { data } = await api.post(ENDPOINTS.modificar, { id: seleccionadoId, descripcion: nombre, detalle: detalleNormalizado || null });
          if (!data.ok) throw new Error(data.message);
        }
        const padreAnterior = seleccionado.dependenciaPadreId == null ? null : Number(seleccionado.dependenciaPadreId);
        if (idPadre !== padreAnterior) {
          const { isConfirmed } = await Swal.fire({
            title: "¿Cambiar dependencia superior?",
            text: "La dependencia y todas sus subdependencias se moverán juntas. También cambiarán los permisos heredados.",
            icon: "question", showCancelButton: true, confirmButtonText: "Sí, cambiar", cancelButtonText: "Cancelar"
          });
          if (!isConfirmed) { await cargar(); return; }
          const { data } = await api.post(ENDPOINTS.vincular, { id: seleccionadoId, dependenciaPadreId: idPadre });
          if (!data.ok) throw new Error(data.message);
          if (idPadre !== null) setAbiertos((prev) => new Set([...prev, idPadre]));
        }
      }
      setModo("editar");
      await cargar();
      Swal.fire({ icon: "success", title: "Cambios guardados", timer: 1400, showConfirmButton: false });
    } catch (error) {
      manejarError(error);
      await cargar();
    } finally {
      setGuardando(false);
    }
  };

  const usuariosFiltrados = (datosPersonal?.usuarios || []).filter((u) => {
    const nombre = [u.apellido, u.apellidos, u.nombres, u.nombre, u.identificacion, u.dni, u.username].filter(Boolean).join(" ").toLocaleLowerCase("es");
    const texto = filtroPersonal.trim().toLocaleLowerCase("es");
    return (!texto || nombre.includes(texto)) &&
      (filtroSede === "todas" || String(u.sedeID ?? "sin_sede") === filtroSede) &&
      (filtroDestino === "todos" || String(u.destino) === filtroDestino) &&
      (tipoPersonal === "todos" || (tipoPersonal === "directos" ? u.esDirecto : !u.esDirecto));
  });
  const gruposPersonal = Object.values(usuariosFiltrados.reduce((acc, u) => {
    const id = String(u.destino);
    if (!acc[id]) acc[id] = { id, nombre: u.dependenciaDescripcion || `Destino ${id}`, usuarios: [] };
    acc[id].usuarios.push(u);
    return acc;
  }, {})).sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));
  const destinosPersonal = (datosPersonal?.arbol ? aplanar([datosPersonal.arbol]) : []);

  // Exporta los usuarios activos del destino consultado, respetando o no los filtros.
  const exportarPersonalExcel = (soloFiltrados = true) => {
    if (!datosPersonal) return;
    const usuarios = soloFiltrados ? usuariosFiltrados : (datosPersonal.usuarios || []);
    if (!usuarios.length) {
      Swal.fire("Sin resultados", "No hay usuarios para exportar con la selección actual.", "info");
      return;
    }

    // Evita interpretar identificaciones como números y fórmulas de Excel.
    const textoSeguro = (valor) => {
      const texto = String(valor ?? "");
      return /^[=+\-@\t\r]/.test(texto) ? `'${texto}` : texto;
    };
    const nombreDestino = datosPersonal.dependencia?.descripcion || `Destino ${modalPersonal}`;
    const filas = [...usuarios].sort((a, b) =>
      String(a.dependenciaDescripcion || "").localeCompare(String(b.dependenciaDescripcion || ""), "es") ||
      String(a.sedeID ?? "").localeCompare(String(b.sedeID ?? ""), "es") ||
      String(a.apellido || a.apellidos || "").localeCompare(String(b.apellido || b.apellidos || ""), "es")
    ).map((u) => ({
      "Dependencia": textoSeguro(u.dependenciaDescripcion || `Destino ${u.destino}`),
      "ID destino": u.destino ?? "",
      "Sede": textoSeguro(SEDES[String(u.sedeID ?? "sin_sede")] || `Sede ${u.sedeID}`),
      "Apellido": textoSeguro(u.apellido ?? u.apellidos ?? ""),
      "Nombre": textoSeguro(u.nombres ?? u.nombre ?? u.username ?? ""),
      "Identificación": textoSeguro(u.identificacion ?? u.dni ?? ""),
      "Asignación": u.esDirecto ? "Directo" : "Subordinado",
    }));

    const libro = XLSX.utils.book_new();
    const hoja = XLSX.utils.json_to_sheet(filas);
    hoja['!cols'] = [
      { wch: 42 }, { wch: 12 }, { wch: 18 }, { wch: 25 },
      { wch: 30 }, { wch: 20 }, { wch: 16 },
    ];
    hoja['!autofilter'] = { ref: hoja['!ref'] };
    XLSX.utils.book_append_sheet(libro, hoja, "Usuarios activos");

    const resumen = [
      ["Dependencia consultada", nombreDestino],
      ["Modalidad", soloFiltrados ? "Filtros aplicados" : "Todos los usuarios de la estructura"],
      ["Usuarios exportados", usuarios.length],
      ["Sede", soloFiltrados && filtroSede !== "todas" ? (SEDES[filtroSede] || `Sede ${filtroSede}`) : "Todas"],
      ["Dependencia filtrada", soloFiltrados && filtroDestino !== "todos" ? (destinosPersonal.find(d => String(d.id) === filtroDestino)?.descripcion || filtroDestino) : "Todas"],
      ["Tipo de asignación", soloFiltrados ? tipoPersonal : "todos"],
    ];
    const hojaResumen = XLSX.utils.aoa_to_sheet(resumen);
    hojaResumen['!cols'] = [{ wch: 25 }, { wch: 65 }];
    XLSX.utils.book_append_sheet(libro, hojaResumen, "Resumen");

    const archivo = `Personal_Destino_${modalPersonal}_${soloFiltrados ? "Filtrado" : "Completo"}.xlsx`;
    XLSX.writeFile(libro, archivo);
  };


  return (
    <main className="ad-contenedor">
      <header className="ad-encabezado">
        <div><button type="button" className="ad-boton ad-boton-secundario ad-volver" onClick={() => navigate("/usuarioLogueado")}>← Volver</button><span className="ad-etiqueta">SIGEDI · Administración</span><h2>Jerarquía de dependencias</h2><p>Organizá los destinos institucionales y sus vínculos.</p></div>
        <button className="ad-boton ad-boton-secundario" type="button" onClick={cargar} disabled={cargando}>↻ Actualizar</button>
      </header>

      {!cargando && !raizValida && <div className="ad-alerta">No existe la dependencia ID 1. Creala primero en la base de datos como Dirección General; este administrador mantiene ese ID como raíz obligatoria.</div>}

      <div className="ad-selector-vista" role="group" aria-label="Tipo de vista">
        <button type="button" className={`ad-boton ${vista === "lista" ? "ad-boton-primario" : "ad-boton-secundario"}`} onClick={() => setVista("lista")}>☷ Vista de lista</button>
        <button type="button" className={`ad-boton ${vista === "cuadros" ? "ad-boton-primario" : "ad-boton-secundario"}`} onClick={() => setVista("cuadros")}>▦ Vista de raíz e hijos</button>
      </div>
      {vista === "cuadros" && (
        <section className="ad-panel ad-panel-organigrama">
          <div className="ad-panel-titulo"><h3>Raíz e hijos · Organigrama visual</h3><span className="ad-contador">{planos.length} destinos · {arbol.reduce((s, n) => s + cantidad(n.personalTotal), 0)} usuarios activos en estructuras raíz</span></div>
          <div className="ad-filtro-niveles">
            <div className="ad-filtro-niveles-cabecera">
              <strong>Niveles visibles</strong>
              <div className="ad-filtro-niveles-acciones">
                <button type="button" onClick={() => setNivelesOcultos(new Set())}>Mostrar todos</button>
                <button type="button" onClick={() => setNivelesOcultos(new Set(Array.from({ length: totalNiveles }, (_, i) => i + 1).filter((n) => n > 2)))}>Solo niveles 1 y 2</button>
              </div>
            </div>
            <div className="ad-filtro-niveles-opciones">
              {Array.from({ length: totalNiveles }, (_, i) => i + 1).map((nivel) => (
                <label key={nivel} className="ad-filtro-nivel">
                  <input type="checkbox" checked={nivelesVisibles.has(nivel)} disabled={nivel === 1}
                    onChange={() => alternarNivel(nivel)} />
                  Nivel {nivel}{nivel === 1 ? " · Raíz" : ""}
                </label>
              ))}
            </div>
            <small>Al ocultar un nivel, también se ocultan sus descendientes en esa rama. Los vínculos no se modifican.</small>
          </div>
          <p className="ad-ayuda">Arrastrá una tarjeta sobre la dependencia que será su nueva superior. Hacé clic en cualquier tarjeta para editarla en el panel inferior. La Dirección General (ID 1) permanece como raíz.</p>
          {cargando ? <p className="ad-vacio">Cargando organigrama...</p> : (
            <div className="ad-organigrama-scroll">
              <div className="ad-organigrama">
                {arbol.map((nodo) => (
                  <TarjetaJerarquia key={nodo.id} nodo={nodo} seleccionadoId={seleccionadoId} seleccionar={seleccionar} verPersonal={verPersonal}
                    arrastradoId={arrastradoId} setArrastradoId={setArrastradoId} destinoId={destinoId}
                    setDestinoId={setDestinoId} soltar={moverPorArrastre} bloqueadosAlArrastrar={bloqueadosAlArrastrar}
                    nivel={1} nivelesVisibles={nivelesVisibles} />
                ))}
              </div>
            </div>
          )}
        </section>
      )}
      <div className={`ad-columnas ${vista === "cuadros" ? "ad-columnas-cuadros" : ""}`}>

        <section className="ad-panel" style={vista === "cuadros" ? {display:"none"} : undefined}>
          <div className="ad-panel-titulo"><h3>Organigrama</h3><span className="ad-contador">{planos.length} destinos</span></div>
          <input className="ad-input" type="search" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar nombre o dependencia..." />
          <div className="ad-arbol">
            {cargando ? <p className="ad-vacio">Cargando dependencias...</p> : visibles.length ? visibles.map((nodo) => <NodoArbol key={nodo.id} nodo={nodo} seleccionado={seleccionadoId} onSeleccionar={seleccionar} abiertos={abiertos} alternar={alternar} busqueda={busqueda} />) : <p className="ad-vacio">No se encontraron dependencias.</p>}
          </div>
          <div className="ad-panel-acciones"><button type="button" className="ad-boton ad-boton-primario" onClick={() => iniciarCreacion(false)} disabled={!raizValida}>+ Nuevo destino</button></div>
        </section>

        <section className="ad-panel ad-detalle">
          <div className="ad-panel-titulo"><h3>{modo === "crear" ? "Crear nuevo destino" : "Editar dependencia"}</h3>{seleccionado && modo === "editar" && <span className="ad-contador">ID {seleccionado.id}</span>}</div>
          {(seleccionado || modo === "crear") ? (
            <form onSubmit={guardar}>
              {modo === "editar" && seleccionado && <div className="ad-resumen"><strong>{seleccionado.descripcion}</strong><span>{(seleccionado.hijos || []).length} subdependencias directas</span><PersonalDependencia nodo={seleccionado} /><button type="button" className="ad-ver-personal" onClick={() => verPersonal(seleccionado.id)}>👥 Ver usuarios de esta dependencia y subordinadas</button></div>}
              <label className="ad-label" htmlFor="ad-descripcion">Nombre del destino</label>
              <input id="ad-descripcion" className="ad-input" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} maxLength={100} required placeholder="Ej.: División Desarrollo" />
              <label className="ad-label" htmlFor="ad-detalle">Descripción detallada del destino</label>
              <textarea id="ad-detalle" className="ad-input ad-textarea" value={detalle} onChange={(e) => setDetalle(e.target.value)} rows={5} placeholder="Describí las funciones, responsabilidades y características de esta dependencia..." />
              <p className="ad-ayuda">Campo opcional. Podés dejarlo vacío o modificarlo cuando lo necesites.</p>
              <label className="ad-label" htmlFor="ad-padre">Depende directamente de</label>
              <select id="ad-padre" className="ad-input" value={padreId} onChange={(e) => setPadreId(e.target.value)} disabled={modo === "editar" && seleccionadoId === ROOT_ID}>
                {modo === "editar" && seleccionadoId === ROOT_ID && <option value="">Sin superior (raíz principal)</option>}
                {opcionesPadre.map((d) => <option key={d.id} value={d.id}>{"— ".repeat(d.nivel)}{d.descripcion} (ID {d.id})</option>)}
              </select>
              <p className="ad-ayuda">Seleccioná el superior directo. Las dependencias subordinadas conservarán su estructura al mover este destino.</p>
              <div className="ad-botones">
                <button className="ad-boton ad-boton-primario" type="submit" disabled={guardando || !raizValida}>{guardando ? "Guardando..." : modo === "crear" ? "Crear dependencia" : "Guardar cambios"}</button>
                {modo === "crear" && <button className="ad-boton ad-boton-secundario" type="button" onClick={() => setModo("editar")}>Cancelar</button>}
              </div>
            </form>
          ) : <p className="ad-vacio">Seleccioná una dependencia en el organigrama.</p>}
          {modo === "editar" && seleccionado && <div className="ad-extra"><h4>Acciones rápidas</h4><button className="ad-boton ad-boton-secundario" type="button" onClick={() => iniciarCreacion(true)}>+ Crear subdependencia aquí</button><p className="ad-ayuda">Para reubicar una dependencia, seleccionála y cambiá el campo “Depende directamente de”.</p></div>}
        </section>
      </div>
      {modalPersonal !== null && (
        <div className="ad-modal-fondo" onMouseDown={(e) => { if (e.target === e.currentTarget) cerrarPersonal(); }}>
          <section className="ad-modal-personal" role="dialog" aria-modal="true" aria-label="Personal por dependencia">
            <div className="ad-modal-cabecera">
              <div><h3>👥 Personal activo por dependencia</h3><p>{datosPersonal?.dependencia?.descripcion || planos.find(d => d.id === modalPersonal)?.descripcion || `Destino ${modalPersonal}`}</p></div>
              <button type="button" className="ad-modal-cerrar" onClick={cerrarPersonal} aria-label="Cerrar">✕</button>
            </div>
            {cargandoPersonal ? <p className="ad-vacio">Consultando usuarios activos...</p> : errorPersonal ? (
              <div className="ad-alerta">{errorPersonal}<div style={{marginTop:10}}><button type="button" className="ad-boton ad-boton-secundario" onClick={() => verPersonal(modalPersonal)}>Reintentar</button></div></div>
            ) : datosPersonal && <>
              <div className="ad-modal-resumen">
                <div><b>{cantidad(datosPersonal.resumen?.totalUsuarios)}</b><span>Usuarios activos totales</span></div>
                <div><b>{cantidad(datosPersonal.resumen?.usuariosDirectos)}</b><span>Usuarios directos</span></div>
                <div><b>{cantidad(datosPersonal.resumen?.usuariosSubordinados)}</b><span>Usuarios subordinados</span></div>
                <div><b>{cantidad(datosPersonal.resumen?.totalDependencias)}</b><span>Destinos incluidos</span></div>
              </div>
              <div className="ad-modal-sedes">{Object.entries(datosPersonal.resumen?.porSede || {}).map(([id, v]) => (
                <span key={id}>{SEDES[id] || `Sede ${id}`}: <b>{cantidad(v.cantidad)}</b> usuarios</span>
              ))}</div>
              <div className="ad-modal-filtros">
                <input className="ad-input" type="search" value={filtroPersonal} onChange={e => setFiltroPersonal(e.target.value)} placeholder="Buscar nombre, apellido o identificación" />
                <select className="ad-input" value={filtroSede} onChange={e => setFiltroSede(e.target.value)} aria-label="Filtrar por sede">
                  <option value="todas">Todas las sedes</option>
                  {Object.keys(datosPersonal.resumen?.porSede || {}).map(id => <option key={id} value={id}>{SEDES[id] || `Sede ${id}`}</option>)}
                </select>
                <select className="ad-input" value={filtroDestino} onChange={e => setFiltroDestino(e.target.value)} aria-label="Filtrar por destino">
                  <option value="todos">Todas las dependencias</option>
                  {destinosPersonal.map(d => <option key={d.id} value={String(d.id)}>{"— ".repeat(d.nivel)}{d.descripcion}</option>)}
                </select>
                <select className="ad-input" value={tipoPersonal} onChange={e => setTipoPersonal(e.target.value)} aria-label="Filtrar tipo de asignación">
                  <option value="todos">Directos y subordinados</option>
                  <option value="directos">Solo directos del destino elegido</option>
                  <option value="subordinados">Solo subordinados</option>
                </select>
              </div>
              <div className="ad-exportar-personal">
                <p className="ad-modal-resultado">Mostrando {usuariosFiltrados.length} de {datosPersonal.usuarios.length} usuarios activos · Agrupados por destino y sede</p>
                <div className="ad-exportar-acciones">
                  <button type="button" className="ad-boton ad-boton-primario" onClick={() => exportarPersonalExcel(true)} disabled={!usuariosFiltrados.length}>⬇ Excel filtrado</button>
                  <button type="button" className="ad-boton ad-boton-secundario" onClick={() => exportarPersonalExcel(false)}>⬇ Excel completo</button>
                </div>
              </div>
              <div className="ad-modal-listado">
                {gruposPersonal.length === 0 && <p className="ad-vacio">No hay usuarios que coincidan con los filtros.</p>}
                {gruposPersonal.map(grupo => <section className="ad-grupo-personal" key={grupo.id}>
                  <h4>{grupo.nombre} <small>· ID {grupo.id} · {grupo.usuarios.length} usuarios</small></h4>
                  {Object.entries(grupo.usuarios.reduce((acc, u) => {
                    const sede = String(u.sedeID ?? "sin_sede");
                    (acc[sede] ||= []).push(u);
                    return acc;
                  }, {})).sort(([a], [b]) => a.localeCompare(b)).map(([sede, usuarios]) => <div key={sede} className="ad-grupo-sede">
                    <h5>{SEDES[sede] || `Sede ${sede}`} · {usuarios.length} usuarios</h5>
                    <div className="ad-tabla-scroll"><table className="ad-tabla-usuarios"><thead><tr><th>Apellido y nombre</th><th>Identificación</th><th>Asignación</th></tr></thead><tbody>
                      {usuarios.map((u, i) => <tr key={`${u.id ?? u.identificacion ?? u.dni ?? i}-${i}`}>
                        <td>{[u.apellido ?? u.apellidos, u.nombres ?? u.nombre].filter(Boolean).join(", ") || u.username || "Sin nombre"}</td>
                        <td>{u.identificacion ?? u.dni ?? "—"}</td>
                        <td>{u.esDirecto ? "Directo" : "Subordinado"}</td>
                      </tr>)}
                    </tbody></table></div>
                  </div>)}
                </section>)}
              </div>
            </>}
          </section>
        </div>
      )}
    </main>
  );
}
